<template>
  <div>
    <div ref="carousel" class="carousel" @resize="onResize">
      <div
        ref="content"
        class="carousel__content"
        @mousedown="dragStart"
        @touchstart="dragStart"
        @touchmove="dragMoving"
        @touchend="dragEnd"
      >
        <slot></slot>
      </div>
      <div v-if="paginationBullets.length" class="carousel__controls">
        <z-icon
          size="medium"
          :icon="['far', 'chevron-left']"
          color="primary"
          @click="prev()"
          class="carousel__controls-arrow"
        />

        <div class="carousel__pagination">
          <div
            v-for="(item, index) in paginationBullets"
            :key="index"
            class="carousel__pagination-bullet"
            :role="`carousel pagination`"
            :aria-label="`go to carousel section ${index + 1}`"
            :class="{ 'carousel__pagination-bullet--active': sectionIndex === index }"
            @click="selectCarouselSection(index)"
          ></div>
        </div>
        <z-icon
          size="medium"
          :icon="['far', 'chevron-right']"
          color="primary"
          @click="next()"
          class="carousel__controls-arrow"
        />
      </div>
    </div>
  </div>
</template>
<script>
export default {
  props: {
    autoPlay: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dragStartPosition: 0,
      xPosition: 0,
      paginationBullets: [],
      sectionIndex: 0,
      autoPlayInterval: null,
    };
  },
  mounted() {
    window.addEventListener('resize', this.onResize, false);
    this.updatePaginationBullets();
    this.startAutoPlayCarousel();
  },
  destroyed() {
    window.removeEventListener('resize', this.onResize);
    clearInterval(this.autoPlayInterval);
  },
  methods: {
    translate() {
      return `translate3D(${(this.xPosition) * -1}px, 0, 0)`;
    },
    onResize() {
      this.updatePaginationBullets();
      this.updateIndexSectionAccordingToCarouselPosition();
    },
    updatePaginationBullets() {
      const { content, carousel } = this.$refs;
      let bulletsTotal = Math.ceil(content.scrollWidth / carousel.offsetWidth);

      if (bulletsTotal > content.children.length) {
        bulletsTotal = content.children.length;
      }

      this.paginationBullets = !bulletsTotal || bulletsTotal < 2 ? [] : new Array(bulletsTotal);
    },
    dragStart({ type, touches, clientX }) {
      const { content } = this.$refs;

      if (type === 'touchstart') {
        this.dragStartPosition = touches[0].clientX;
        return;
      }

      this.dragStartPosition = clientX;
      content.addEventListener('mousemove', this.dragMoving, false);
      content.addEventListener('mouseup', this.dragEnd, false);
      content.addEventListener('mouseleave', this.dragEnd, false);
    },
    dragMoving({ clientX, touches }) {
      const xPos = touches ? touches[0].clientX : clientX;
      const movedDistance = this.dragStartPosition - xPos;
      const newXPosition = this.xPosition + movedDistance;
      this.dragStartPosition = xPos;

      this.moveCarousel(newXPosition);
    },
    dragEnd({ type }) {
      const { content, carousel } = this.$refs;

      if (type === 'mouseup' || type === 'mouseleave') {
        content.removeEventListener('mouseleave', this.dragEnd);
        content.removeEventListener('mouseup', this.dragEnd);
        content.removeEventListener('mousemove', this.dragMoving);
      }

      if (this.xPosition < 0 || carousel.offsetWidth > content.scrollWidth) {
        this.selectCarouselSection(0);
      } else {
        this.updateIndexSectionAccordingToCarouselPosition();
      }

      if (this.autoPlayInterval) {
        clearInterval(this.autoPlayInterval);
      }
    },
    updateIndexSectionAccordingToCarouselPosition() {
      const { carousel, content } = this.$refs;
      const sectionWidth = carousel.offsetWidth;
      const contentWidth = content.scrollWidth;
      let currentIndex = Math.round(this.xPosition / sectionWidth);

      if (sectionWidth * currentIndex > contentWidth) {
        currentIndex = Math.floor(contentWidth / sectionWidth);
      }

      this.selectCarouselSection(currentIndex);
    },
    next() {
      if (this.sectionIndex + 1 >= this.paginationBullets.length) {
        return;
      }

      this.selectCarouselSection(this.sectionIndex + 1);
    },
    prev() {
      if (this.sectionIndex === 0) {
        return;
      }

      this.selectCarouselSection(this.sectionIndex - 1);
    },
    selectCarouselSection(index) {
      this.sectionIndex = index;
      this.moveCarousel(this.getFirstItemXPosFromSection(index), true);
    },
    getFirstItemXPosFromSection(index) {
      let xPosFirstItem = 0;
      const { content, carousel } = this.$refs;
      const slides = content.children;
      const sectionXPos = carousel.offsetWidth * index;

      for (let i = 0; i < slides.length; i += 1) {
        const element = slides[i];
        const elementStyle = getComputedStyle(element);
        const elementMarginLeft = parseFloat(elementStyle.marginLeft) || 0;
        const elementTotalWidth = element.offsetWidth + elementMarginLeft;
        const isElementVisibleAndIsNotFromThePreviousSection = (
          element.offsetLeft + elementTotalWidth > sectionXPos && i !== index - 1
        );
        const isLastElementFromCarousel = i === (slides.length - 1);

        if (isElementVisibleAndIsNotFromThePreviousSection || isLastElementFromCarousel) {
          xPosFirstItem = element.offsetLeft - elementMarginLeft;

          break;
        }
      }

      return xPosFirstItem;
    },
    moveCarousel(xPos, animate) {
      if (xPos === this.xPosition) { return; }

      this.xPosition = xPos;
      const { content } = this.$refs;

      requestAnimationFrame(() => {
        if (animate) {
          content.style['transition-duration'] = '300ms';

          setTimeout(() => {
            content.style['transition-duration'] = '0ms';
          }, 400);
        }

        content.style.transform = this.translate();
      });
    },
    startAutoPlayCarousel() {
      if (!this.autoPlay) {
        return;
      }

      this.autoPlayInterval = setInterval(() => {
        this.next();

        if (this.sectionIndex - 1 === this.paginationBullets.lenth) {
          clearInterval(this.autoPlayInterval);
        }
      }, 20000);
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@quero/zilla-core/src/utils/variables.scss';

.carousel {
  overflow: hidden;
  padding: var(--size-extra-small);
  width: 100%;
}

.carousel__content {
  display: inline-block;
  font-size: 0;
  position: relative;
  transition-duration: 0ms;
  transition-property: transform;
  user-select: none;
  white-space: nowrap;
  width: inherit;

  & > * {
    display: inline-block !important;
  }
}

.carousel__controls {
  align-items: center;
  display: flex;
  justify-content: center;
  margin-top: var(--size-giant);
  position: relative;
  text-align: center;

  @media (min-width: $screen-desktop) {
    margin-top: var(--size-extra-large);
  }
}

.carousel__pagination {
  display: inline-block;
  font-size: 0;
  margin: 0 var(--size-medium);
}

.carousel__pagination-bullet {
  background-color: var(--color-gray-200);
  border-radius: 50%;
  cursor: pointer;
  display: inline-block;
  height: var(--size-small);
  margin-right: var(--size-small);
  width: var(--size-small);

  &:last-child {
    margin-right: 0;
  }
}

.carousel__pagination-bullet--active {
  background-color: var(--color-primary);
}

.carousel__controls-arrow {
  cursor: pointer;
}
</style>
